import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  ShieldCheck,
  AlertCircle,
  Lock,
  Mail,
  User,
  Phone,
  ArrowRight,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { invitationService } from '../lib/invitationService';
import { ValidateTokenResponse } from '../types/invitation';
import { GuestChartMigrationModal } from './GuestChartMigrationModal';
import { UserProfile } from '../lib/firebase';

interface InvitationRedemptionViewProps {
  token: string;
  onSuccess: (user: UserProfile) => void;
  onCancel: () => void;
  lang?: string;
}

export const InvitationRedemptionView: React.FC<InvitationRedemptionViewProps> = ({
  token,
  onSuccess,
  onCancel,
  lang = 'en',
}) => {
  const isHi = lang === 'hi';

  const [validating, setValidating] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<ValidateTokenResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Form inputs
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mobile, setMobile] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Migration modal state
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [createdUser, setCreatedUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    let isMounted = true;
    const checkToken = async () => {
      setValidating(true);
      setValidationError(null);
      const res = await invitationService.validateToken(token);
      if (!isMounted) return;
      setValidating(false);

      if (res.valid) {
        setTokenInfo(res);
      } else {
        setValidationError(res.reason || 'This invitation token is invalid or has expired.');
      }
    };

    checkToken();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!name.trim()) {
      setSubmitError(isHi ? 'कृपया अपना नाम दर्ज करें' : 'Please enter your name');
      return;
    }
    if (!password || password.length < 6) {
      setSubmitError(isHi ? 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए' : 'Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError(isHi ? 'पासवर्ड मेल नहीं खाते' : 'Passwords do not match');
      return;
    }

    if (!tokenInfo?.boundEmail) {
      setSubmitError('Invalid invitation state');
      return;
    }

    setSubmitting(true);
    const res = await invitationService.redeemInvitation(token, {
      name: name.trim(),
      email: tokenInfo.boundEmail,
      password,
      mobile: mobile.trim() || undefined,
    });
    setSubmitting(false);

    if (!res.success) {
      setSubmitError(res.error || 'Failed to complete registration');
      return;
    }

    // Store auth token
    if (res.token) {
      localStorage.setItem('astroveda_token', res.token);
    }

    // Check if there are saved guest kundalis to migrate
    const guestCharts = localStorage.getItem('astroveda_saved_kundalis_guest');
    let hasGuestCharts = false;
    try {
      const parsed = guestCharts ? JSON.parse(guestCharts) : [];
      if (Array.isArray(parsed) && parsed.length > 0) {
        hasGuestCharts = true;
      }
    } catch {}

    const profile: UserProfile = res.user;
    if (hasGuestCharts) {
      setCreatedUser(profile);
      setShowMigrationModal(true);
    } else {
      onSuccess(profile);
    }
  };

  if (validating) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
        <div className="text-center space-y-3">
          <RefreshCw className="w-8 h-8 mx-auto text-amber-400 animate-spin" />
          <p className="text-sm font-serif text-slate-200">
            {isHi ? 'आमंत्रण की वैधता जांची जा रही है...' : 'Validating your invitation token...'}
          </p>
        </div>
      </div>
    );
  }

  if (validationError || !tokenInfo?.valid) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fade-in">
        <div className="w-full max-w-md bg-slate-900 border border-red-500/30 rounded-2xl p-6 shadow-2xl text-slate-100 text-center space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400">
            <AlertCircle className="w-7 h-7" />
          </div>
          <h3 className="text-lg font-serif font-bold text-red-300">
            {isHi ? 'अमान्य अथवा समाप्त आमंत्रण' : 'Invalid or Expired Invitation'}
          </h3>
          <p className="text-xs text-slate-300 leading-relaxed">
            {validationError}
          </p>
          <div className="pt-2 flex flex-col gap-2">
            <button
              onClick={onCancel}
              className="w-full py-2.5 px-4 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium rounded-xl text-xs transition"
            >
              {isHi ? 'अतिथि के रूप में अन्वेषण जारी रखें' : 'Continue as Guest'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fade-in">
        <div className="relative w-full max-w-md bg-slate-900 border border-amber-500/40 rounded-2xl shadow-2xl p-6 text-slate-100 overflow-hidden">
          {/* Header */}
          <div className="text-center pb-4 border-b border-slate-800">
            <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Invitation Confirmed ({tokenInfo.allocatedTier || 'BASIC'})</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-slate-100">
              {isHi ? 'खाता सृजन एवं आमंत्रण स्वीकृति' : 'Activate Your Circle Account'}
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              {isHi ? 'वेदांग ज्योतिष में आपका स्वागत है' : 'Welcome to VedangJyotisham'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-4 space-y-3.5">
            {submitError && (
              <div className="p-3 bg-red-950/60 border border-red-800 rounded-xl flex items-center space-x-2 text-red-300 text-xs">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            {/* Locked Email Field */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                {isHi ? 'सत्यापित ईमेल पता' : 'Bound Email Address (Locked)'}
              </label>
              <div className="relative">
                <input
                  type="email"
                  disabled
                  value={tokenInfo.boundEmail || ''}
                  className="w-full pl-9 pr-3 py-2 bg-slate-950/60 border border-slate-800 rounded-lg text-slate-400 text-xs cursor-not-allowed font-mono"
                />
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                {isHi ? 'पूरा नाम' : 'Full Name'} *
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Pt. Devadatta"
                  className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-xs focus:outline-none focus:border-amber-400"
                />
                <User className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Mobile (Optional) */}
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                {isHi ? 'मोबाइल नंबर (वैकल्पिक)' : 'Mobile Phone (Optional)'}
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="+91 9876543210"
                  className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-xs focus:outline-none focus:border-amber-400"
                />
                <Phone className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                {isHi ? 'पासवर्ड बनाएँ' : 'Create Password'} *
              </label>
              <div className="relative">
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-xs focus:outline-none focus:border-amber-400"
                />
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                {isHi ? 'पासवर्ड की पुष्टि करें' : 'Confirm Password'} *
              </label>
              <div className="relative">
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-xs focus:outline-none focus:border-amber-400"
                />
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 px-4 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-semibold rounded-xl text-xs transition shadow-lg shadow-amber-900/30 flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {submitting ? (
                  <span>{isHi ? 'खाता सृजित हो रहा है...' : 'Activating Account...'}</span>
                ) : (
                  <>
                    <span>{isHi ? 'आमंत्रण स्वीकारें एवं खाता बनाएँ' : 'Redeem & Complete Registration'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Migration Modal Trigger if guest kundalis exist */}
      {showMigrationModal && createdUser && (
        <GuestChartMigrationModal
          isOpen={showMigrationModal}
          onClose={() => {
            setShowMigrationModal(false);
            onSuccess(createdUser);
          }}
          targetUser={createdUser}
          lang={lang}
        />
      )}
    </>
  );
};

